import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { CANvisionContext, CANvisionContextValue, ClaimedEventInfo } from './CANvisionContext';
import { useSites } from 'containers/pages/CANvision/hooks/useSites';
import { useCameras } from 'containers/pages/CANvision/hooks/useCameras';
import { useCANVMSSocket } from 'containers/pages/CANvision/hooks/useCANVMSSocket';
import { EventNotification, StatusChangeNotification } from '../components/EventNotification';
import { useSoundManager } from '../components/Dashboard/SoundManager';
import { getHealth, getDashboard } from 'api/canvms';
import type { HealthStatus, DashboardData, IncidentSeverity } from '../types/canvision.types';
import type {
  VMSEventPayload,
  VMSIncidentCreatedPayload,
  VMSStatusChangePayload,
  VMSEventUpdatedPayload,
  VMSIncidentUpdatedPayload,
  VMSEventClaimedPayload,
  VMSEventReleasedPayload,
  VMSClaimRejectedPayload,
} from 'containers/pages/CANvision/hooks/useCANVMSSocket';
import useNotificationProvider from 'hooks/notificationBar/useNotificationProvider';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Polling interval for system health and dashboard stats (ms). */
const POLL_INTERVAL_MS = 30_000;

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface CANvisionProviderProps {
  children: React.ReactNode;
}

/**
 * Top-level provider that owns all shared CANvision state.
 *
 * Responsibilities:
 *  - Site list fetching via `useSites`
 *  - Camera list for the selected site via `useCameras`
 *  - Single WebSocket connection via `useCANVMSSocket`
 *  - Polling `/health` and `/dashboard` every 30 s
 *  - Shared UI state (drawers, attention feed coordination)
 *
 * Wrap the CANvision route tree with this provider once P1-05 integrates
 * it into `CANvisionLayout`.
 */
export function CANvisionProvider({ children }: CANvisionProviderProps) {
  // -- Sites ----------------------------------------------------------------
  const {
    sites,
    loading: sitesLoading,
    error: sitesError,
    refetch: refetchSites,
  } = useSites();

  // -- Command Center Selection ---------------------------------------------
  const [selectedCommandCenterId, setSelectedCommandCenterId] = useState<number | null>(null);

  // -- Site Selection -------------------------------------------------------
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);

  // -- Cameras (for selected site) ------------------------------------------
  const {
    cameras,
    loading: camerasLoading,
    error: camerasError,
    refetch: refetchCameras,
  } = useCameras(selectedSiteId);

  // -- WebSocket Event State (exposed to consumers via context) --------------
  const [lastEvent, setLastEvent] = useState<VMSEventPayload | null>(null);
  const [lastIncident, setLastIncident] = useState<VMSIncidentCreatedPayload | null>(null);
  const [lastStatusChange, setLastStatusChange] = useState<VMSStatusChangePayload | null>(null);
  const [lastEventUpdate, setLastEventUpdate] = useState<VMSEventUpdatedPayload | null>(null);
  const [lastIncidentUpdate, setLastIncidentUpdate] = useState<VMSIncidentUpdatedPayload | null>(null);

  // -- Event Claim State (hard lock map) --------------------------------------
  const [claimedEvents, setClaimedEvents] = useState<Map<number, ClaimedEventInfo>>(
    () => new Map()
  );

  // -- Notification Provider (for claim rejection/error toasts) ---------------
  const { notifyWarning } = useNotificationProvider();
  const notifyWarningRef = useRef(notifyWarning);
  useEffect(() => {
    notifyWarningRef.current = notifyWarning;
  }, [notifyWarning]);

  // -- Toast Notification State -----------------------------------------------
  const [eventNotification, setEventNotification] = useState<VMSEventPayload | null>(null);
  const [statusNotification, setStatusNotification] = useState<VMSStatusChangePayload | null>(null);

  // -- Sound Manager -----------------------------------------------------------
  const { playSound, isMuted: isSoundMuted, toggleMute: toggleSoundMute } = useSoundManager();

  // Store playSound in a ref so the socket callback can use it without
  // causing the socket hook to reconnect when the reference changes.
  const playSoundRef = useRef(playSound);
  useEffect(() => {
    playSoundRef.current = playSound;
  }, [playSound]);

  // -- Refetch helpers (need forward declaration for socket callbacks) --------
  // fetchDashboard and refetchSites are defined below; store stable refs so
  // socket callbacks can call them without causing the socket hook to reconnect.
  const refetchDashboardRef = useRef<() => void>(() => {});
  const refetchSitesRef = useRef<() => void>(() => {});

  // -- WebSocket (single connection) ----------------------------------------
  const siteIds = useMemo(() => sites.map((s) => s.id), [sites]);

  const socketConnection = useCANVMSSocket({
    siteIds,
    enabled: sites.length > 0,
    onEvent: useCallback((event: VMSEventPayload) => {
      setLastEvent(event);
      setEventNotification(event);
      refetchDashboardRef.current();
    }, []),
    onIncidentCreated: useCallback((payload: VMSIncidentCreatedPayload) => {
      if (!payload?.incident) {
        console.warn('[CANvision/Provider] onIncidentCreated received invalid payload:', payload);
        return;
      }
      setLastIncident(payload);
      refetchDashboardRef.current();
      // Play incident notification sound based on severity
      if (payload.incident.severity) {
        playSoundRef.current(payload.incident.severity as IncidentSeverity);
      }
    }, []),
    onStatusChange: useCallback((status: VMSStatusChangePayload) => {
      setLastStatusChange(status);
      setStatusNotification(status);
      refetchSitesRef.current();
      refetchDashboardRef.current();
    }, []),
    onEventUpdated: useCallback((event: VMSEventUpdatedPayload) => {
      console.debug('[CANvisionProvider] Event updated:', event.id, '→', event.status);
      setLastEventUpdate(event);
      refetchDashboardRef.current();
    }, []),
    onIncidentUpdated: useCallback((payload: VMSIncidentUpdatedPayload) => {
      if (!payload?.incident) {
        console.warn('[CANvisionProvider] onIncidentUpdated received invalid payload:', payload);
        return;
      }
      console.debug('[CANvisionProvider] Incident updated:', payload.incident.id, '→', payload.incident.status);
      setLastIncidentUpdate(payload);
      refetchDashboardRef.current();
    }, []),
    onEventClaimed: useCallback((payload: VMSEventClaimedPayload) => {
      console.debug('[CANvisionProvider] Event claimed:', payload.eventId, 'by', payload.operatorName);
      setClaimedEvents((prev) => {
        const next = new Map(prev);
        next.set(payload.eventId, {
          operatorName: payload.operatorName,
          claimedAt: payload.claimedAt,
        });
        return next;
      });
    }, []),
    onEventReleased: useCallback((payload: VMSEventReleasedPayload) => {
      console.debug('[CANvisionProvider] Event released:', payload.eventId);
      setClaimedEvents((prev) => {
        if (!prev.has(payload.eventId)) return prev;
        const next = new Map(prev);
        next.delete(payload.eventId);
        return next;
      });
    }, []),
    onClaimRejected: useCallback((payload: VMSClaimRejectedPayload) => {
      console.debug('[CANvisionProvider] Claim rejected for event', payload.eventId, '- held by', payload.holder);
      notifyWarningRef.current(`Being reviewed by ${payload.holder}`);
    }, []),
    onClaimError: useCallback((payload: { message: string }) => {
      console.warn('[CANvisionProvider] Claim operation error:', payload.message);
      notifyWarningRef.current('Claim operation failed');
    }, []),
  });

  // -- System Health Polling ------------------------------------------------
  const [systemHealth, setSystemHealth] = useState<HealthStatus | null>(null);
  const [systemHealthLoading, setSystemHealthLoading] = useState(true);
  const healthTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchHealth = useCallback(async () => {
    try {
      const response = await getHealth();
      setSystemHealth(response.data);
    } catch (err) {
      console.error('[CANvision] Failed to fetch system health:', err);
    } finally {
      setSystemHealthLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    healthTimerRef.current = setInterval(fetchHealth, POLL_INTERVAL_MS);
    return () => {
      if (healthTimerRef.current) clearInterval(healthTimerRef.current);
    };
  }, [fetchHealth]);

  // -- Dashboard Stats Polling ----------------------------------------------
  const [dashboardStats, setDashboardStats] = useState<DashboardData | null>(null);
  const [dashboardStatsLoading, setDashboardStatsLoading] = useState(true);
  const dashboardTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await getDashboard();
      setDashboardStats(response.data);
    } catch (err) {
      console.error('[CANvision] Failed to fetch dashboard stats:', err);
    } finally {
      setDashboardStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    dashboardTimerRef.current = setInterval(fetchDashboard, POLL_INTERVAL_MS);
    return () => {
      if (dashboardTimerRef.current) clearInterval(dashboardTimerRef.current);
    };
  }, [fetchDashboard]);

  // -- Wire refetch refs (consumed by socket callbacks above) -----------------
  useEffect(() => {
    refetchDashboardRef.current = fetchDashboard;
  }, [fetchDashboard]);

  useEffect(() => {
    refetchSitesRef.current = refetchSites;
  }, [refetchSites]);

  // -- Attention Feed UI Coordination ---------------------------------------
  const [highlightedCameraId, setHighlightedCameraId] = useState<number | null>(null);
  const [filterByCameraId, setFilterByCameraId] = useState<number | null>(null);

  // -- Shared Drawer States -------------------------------------------------
  const [siteManagerOpen, setSiteManagerOpen] = useState(false);
  const [cameraSettingsOpen, setCameraSettingsOpen] = useState(false);
  const [cameraSetManagerOpen, setCameraSetManagerOpen] = useState(false);
  const [checklistMappingOpen, setChecklistMappingOpen] = useState(false);

  // -- Memoised Context Value -----------------------------------------------
  const value = useMemo<CANvisionContextValue>(
    () => ({
      // Sites
      sites,
      sitesLoading,
      sitesError,
      refetchSites,
      // Command Center
      selectedCommandCenterId,
      setSelectedCommandCenterId,
      // Site Selection
      selectedSiteId,
      setSelectedSiteId,
      // Cameras
      cameras,
      camerasLoading,
      camerasError,
      refetchCameras,
      // WebSocket
      socketConnection,
      // WebSocket event payloads (for consumer subscriptions)
      lastEvent,
      lastIncident,
      lastStatusChange,
      lastEventUpdate,
      lastIncidentUpdate,
      // Event Claim/Release
      claimedEvents,
      claimEvent: socketConnection.claimEvent,
      releaseEvent: socketConnection.releaseEvent,
      // Health
      systemHealth,
      systemHealthLoading,
      // Dashboard
      dashboardStats,
      dashboardStatsLoading,
      // Refetch triggers
      refetchDashboard: fetchDashboard,
      // Sound Manager
      playIncidentSound: playSound,
      isSoundMuted,
      toggleSoundMute,
      // Attention Feed
      highlightedCameraId,
      setHighlightedCameraId,
      filterByCameraId,
      setFilterByCameraId,
      // Drawers
      siteManagerOpen,
      setSiteManagerOpen,
      cameraSettingsOpen,
      setCameraSettingsOpen,
      cameraSetManagerOpen,
      setCameraSetManagerOpen,
      checklistMappingOpen,
      setChecklistMappingOpen,
    }),
    [
      sites,
      sitesLoading,
      sitesError,
      refetchSites,
      selectedCommandCenterId,
      selectedSiteId,
      cameras,
      camerasLoading,
      camerasError,
      refetchCameras,
      socketConnection,
      lastEvent,
      lastIncident,
      lastStatusChange,
      lastEventUpdate,
      lastIncidentUpdate,
      claimedEvents,
      systemHealth,
      systemHealthLoading,
      dashboardStats,
      dashboardStatsLoading,
      fetchDashboard,
      playSound,
      isSoundMuted,
      toggleSoundMute,
      highlightedCameraId,
      filterByCameraId,
      siteManagerOpen,
      cameraSettingsOpen,
      cameraSetManagerOpen,
      checklistMappingOpen,
    ],
  );

  return (
    <CANvisionContext.Provider value={value}>
      {children}

      {/* Real-time event notification toast */}
      <EventNotification
        open={!!eventNotification}
        onClose={() => setEventNotification(null)}
        event={eventNotification}
      />

      {/* Real-time status change notification toast */}
      <StatusChangeNotification
        open={!!statusNotification}
        onClose={() => setStatusNotification(null)}
        statusChange={statusNotification}
      />
    </CANvisionContext.Provider>
  );
}
